import { FeedbackForm, KText } from '@/ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
} from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { aiScoringUtils } from '@/utils/ai-scoring-utils';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { ChevronSelectorVertical } from '@kula-ai/icons/outline/ChevronSelectorVertical';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import { AiScoreExplained } from './AiScoreExplained';
import { aiScoringApi } from '@/api/aiScoring/aiScoring.api';
import { useMutation } from '@tanstack/react-query';
import { CenterSpinner } from '@/components/ats';
import { Stars03 } from '@kula-ai/icons-core';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { cn, Text } from '@kula-ai/components';
import { useShowToast } from '@/hooks';

const { getScoreLevel } = aiScoringUtils;

const BreakDown = ({
  attribute,
  isAllExpanded = false,
  scrollContainerRef,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const itemRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    setIsExpanded(isAllExpanded);
  }, [isAllExpanded]);

  const scrollToContent = () => {
    if (!scrollContainerRef?.current || !itemRef.current) return;

    const scrollContainer = scrollContainerRef.current;
    const itemElement = itemRef.current;

    // Small delay to allow DOM to update after expansion
    setTimeout(() => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = itemElement.getBoundingClientRect();

      // Calculate positions relative to scroll container
      const itemTop =
        itemRect.top - containerRect.top + scrollContainer.scrollTop;
      const itemBottom = itemTop + itemRect.height;

      const containerHeight = containerRect.height;
      const scrollTop = scrollContainer.scrollTop;
      const scrollBottom = scrollTop + containerHeight;

      // Add some padding for better UX
      const padding = 20;

      // Check if the expanded item would be fully visible
      const isFullyVisible =
        itemTop >= scrollTop + padding && itemBottom <= scrollBottom - padding;

      if (!isFullyVisible) {
        // If item is partially above viewport, scroll to show it at top with padding
        if (itemTop < scrollTop + padding) {
          scrollContainer.scrollTo({
            top: Math.max(0, itemTop - padding),
            behavior: 'smooth',
          });
        }
        // If item extends below viewport, scroll to show bottom with padding
        else if (itemBottom > scrollBottom - padding) {
          scrollContainer.scrollTo({
            top: Math.max(0, itemBottom - containerHeight + padding),
            behavior: 'smooth',
          });
        }
      }
    }, 100); // Slightly longer delay to ensure content is rendered
  };

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    if (newExpanded) {
      scrollToContent();
    }
  };

  const { label, description: tooltip, scoreConfig } = attribute ?? {};
  const { reasoning, score_level = '' } = scoreConfig ?? {};
  const { bg } = getScoreLevel(score_level) ?? {};

  return (
    <Flex ref={itemRef} flexDirection="column" gap="12px">
      <Flex
        gap="16px"
        w="100%"
        justifyContent="space-between"
        onClick={handleToggle}
        cursor="pointer"
      >
        <Flex alignItems="center" gap="6px">
          <Icon as={isExpanded ? ChevronDown : ChevronRight} />
          <KText
            color="secondary-900"
            tooltipText={tooltip}
            enableTooltip={!!tooltip}
            isTextTruncatedDefault
          >
            {label}
          </KText>
        </Flex>
        <KText
          variant="heading_4"
          border="1px solid"
          borderColor={bg}
          borderRadius="6px"
          p="3px 6px"
          color="secondary-700"
          h="fit-content"
          minW="50px"
          textAlign="center"
        >
          {score_level?.toUpperCase()}
        </KText>
      </Flex>
      {isExpanded && (
        <Box ref={contentRef} ml="20px">
          <KText whiteSpace="pre-wrap">{reasoning}</KText>
        </Box>
      )}
    </Flex>
  );
};

const AttributeSection = ({ attributes, attribute_scores }) => {
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const scrollContainerRef = useRef(null);

  // First filter attribute_scores to only include those that have matching attributes
  const validAttributeScores = attribute_scores.filter((score) =>
    attributes.some(
      (attribute) =>
        score.attribute_label === attribute.label ||
        score.attribute_id === attribute.id
    )
  );

  // Then map attributes with their matching scores and filter out those without scores
  const attributesWithScores = attributes
    .map((attribute) => {
      const attributeScore = validAttributeScores.find(
        (score) =>
          score.attribute_label === attribute.label ||
          score.attribute_id === attribute.id
      );
      return {
        ...attribute,
        scoreConfig: attributeScore,
      };
    })
    .filter((attribute) => attribute.scoreConfig) // Only keep attributes that have scores
    .sort((a, b) => (a.position || 0) - (b.position || 0)); // Sort by position field

  return (
    <Flex flexDirection="column" gap="16px" w="100%" minH="0" flex="1">
      <Flex w="100%" justifyContent="space-between" flexShrink={0} p="0 12px">
        <KText variant="heading_4" color="secondary-600">
          BREAKDOWN
        </KText>
        <Flex
          alignItems="center"
          gap="6px"
          onClick={() => setIsAllExpanded((prev) => !prev)}
          cursor="pointer"
        >
          <Icon as={ChevronSelectorVertical} />
          <KText variant="heading_4" color="secondary-700">
            {isAllExpanded ? 'Collapse all' : 'Expand all'}
          </KText>
        </Flex>
      </Flex>
      <Flex
        ref={scrollContainerRef}
        flexDirection="column"
        gap="12px"
        overflowY="auto"
        flex="1"
        minH="0"
        p="0 12px"
      >
        {attributesWithScores.map((attribute, index) => (
          <BreakDown
            key={attribute.id || attribute.label || index}
            attribute={attribute}
            isAllExpanded={isAllExpanded}
            scrollContainerRef={scrollContainerRef}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export const AiScoreBreakdown = ({
  data,
  session,
  retest = undefined,
  testAgainNewCandidate = undefined,
  isLoading = false,
  candidateName = '',
  isFromPopover = false,
  isFromQuickAppReview = false,
  onModalStateChange = (_isOpen) => {},
  application_id,
  ats_job_id,
}) => {
  const {
    rating = 0,
    overall_evaluation = '',
    overall_score_level,
    score_level,
    evaluation = '',
    attributes = [],
    attribute_scores = [],
    trace_id,
  } = data ?? {};
  const config = getScoreLevel(overall_score_level ?? score_level);

  const toast = useShowToast();
  const { feJobTeammates } = useKFlags();

  const { mutate: aiFeedback } = useMutation({
    mutationFn: aiScoringApi.aiFeedback,
    onSuccess: () => {
      toast({
        title: 'Feedback submitted successfully',
      });
    },
  });

  const header = () => {
    const getCandidateName = (candidateName) => {
      if (session?.contact) {
        return session.contact.name;
      } else if (session?.contact_resume) {
        return session.contact_resume.filename;
      }
      return candidateName ?? 'Candidate';
    };

    const name = isFromQuickAppReview
      ? feJobTeammates
        ? 'Scoring agent score'
        : 'AI score'
      : feJobTeammates
        ? `Scoring agent score for ${getCandidateName(candidateName)}`
        : `AI score for ${getCandidateName(candidateName)}`;

    return (
      <>
        <Flex justifyContent="space-between" w="100%" flexShrink={0}>
          <Flex alignItems="center" gap="10px">
            <Stars03
              size={20}
              strokeWidth={1.2}
              className="min-w-fit"
              gradient={{
                direction: 'vertical',
                colors: [
                  { offset: '0%', color: '#797DFF' },
                  { offset: '100%', color: '#680AF5' },
                ],
              }}
            />{' '}
            <TruncatedTextWithTooltip
              maxW={isFromPopover ? '420px' : '370px'}
              text={name}
              toolTipVariant="default"
            >
              <Text
                truncate
                variant="h2"
                className={cn(
                  'max-w-[370px] bg-gradient-to-b from-primary-400 to-pastel-3dark bg-clip-text text-transparent',
                  isFromPopover && 'max-w-[420px]'
                )}
              >
                {name}
              </Text>
            </TruncatedTextWithTooltip>
          </Flex>
          {(retest || testAgainNewCandidate) && (
            <Flex gap="8px">
              {testAgainNewCandidate && (
                <Button variant="tertiary" onClick={testAgainNewCandidate}>
                  Test another candidate
                </Button>
              )}
            </Flex>
          )}
        </Flex>
        {(retest || testAgainNewCandidate) && <Divider />}
      </>
    );
  };

  const scoreSection = () => {
    const { bg, label, light, dark } = config ?? {};

    return (
      <Flex w="100%" flexDirection="column" gap="10px" flexShrink={0}>
        <Flex justifyContent="space-between" w="100%">
          <KText
            variant="heading_4"
            bg={light}
            color={dark}
            p="4px 10px"
            borderRadius="32px"
          >
            {rating}%
          </KText>
          <Flex gap="6px" alignItems="center">
            <KText variant="heading_4">Match</KText>
            <KText
              variant="heading_4"
              bg={light}
              color={dark}
              p="4px 6px"
              borderRadius="6px"
            >
              {label}
            </KText>
          </Flex>
        </Flex>
        <Slider value={rating} min={0} max={100} cursor="default">
          <SliderTrack h="6px" bg="primary-25">
            <SliderFilledTrack bg={bg} />
          </SliderTrack>
        </Slider>
        <Flex
          flexDir="column"
          gap="6px"
          p="12px"
          bg="secondary-25"
          borderRadius="sm"
          mt="6px"
        >
          <KText variant="heading_4">Overall evaluation</KText>
          <KText>{overall_evaluation || evaluation}</KText>
        </Flex>
      </Flex>
    );
  };

  if (isLoading)
    return (
      <Flex
        justifyContent="center"
        bg="base-white"
        alignItems="center"
        h="500px"
      >
        <CenterSpinner h="100%" />
      </Flex>
    );

  return (
    <Flex
      flexDirection="column"
      w="100%"
      h="100%"
      minH="0"
      py={isFromPopover || isFromQuickAppReview ? '3px' : '12px'}
    >
      {/* Header - Fixed */}

      <Flex flexDirection="column" gap="16px" flexShrink={0} px="12px">
        {header()}
        {scoreSection()}
      </Flex>

      {/* Scrollable Content */}
      <Flex flexDirection="column" gap="16px" minH="0" mt="16px">
        <AttributeSection
          attributes={attributes}
          attribute_scores={attribute_scores}
        />
      </Flex>

      {/* Footer - Fixed */}
      <Flex
        p="12px"
        w="100%"
        justifyContent="space-between"
        alignItems="center"
        flexShrink={0}
        {...(isFromQuickAppReview && {
          pb: '0px',
        })}
        {...(isFromPopover && {
          mt: '8px',
          borderTop: '1px solid',
          borderColor: 'secondary-100',
        })}
      >
        <FeedbackForm
          onFeedbackSubmit={(
            {
              rating,
              feedback,
              trace_id,
              feedbackable_id,
              ats_job_id,
              is_test_run: _is_test_run,
            },
            onClose?: () => void
          ) => {
            // For test scenarios, we have session with contact_resume but no application_id
            const isTestScenario = session?.contact_resume && !application_id;
            const meta = {
              feature: 'ai scoring',
              ats_job_id,
              is_test_run: isTestScenario ? 'true' : 'false',
              ats_application_id: feedbackable_id,
            };

            aiFeedback({
              rating,
              ...(feedback && { feedback }),
              trace_id,
              ...(feedbackable_id && { feedbackable_id }),
              feedbackable_type: 'AtsApplication',
              meta,
            });
            onClose?.();
          }}
          onModalStateChange={onModalStateChange}
          trace_id={trace_id}
          feedbackable_id={application_id || session?.contact_resume?.id}
          ats_job_id={ats_job_id}
          is_test_run={!!session}
        />
        <AiScoreExplained
          attributes={attributes}
          onModalStateChange={onModalStateChange}
        />
      </Flex>
    </Flex>
  );
};
